import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';

export const DEFAULT_ATTENDANCE_SETTINGS = {
  default_start_time: '08:00',
  default_end_time: '17:00',
  warning_minutes: 10,
  snooze_minutes: 15,
  morning_cutoff_time: '07:00'
};

export const LEAVE_TYPES = [
  { id: 'annual', label: 'Annual Leave', shortLabel: 'Annual', color: 'sky', bg: 'bg-sky-50', text: 'text-sky-700', border: 'border-sky-300' },
  { id: 'sick', label: 'Sick Leave', shortLabel: 'Sick', color: 'rose', bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-300' },
  { id: 'family', label: 'Family Leave', shortLabel: 'Family', color: 'purple', bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300' },
  { id: 'awol', label: 'AWOL (Unexcused)', shortLabel: 'AWOL', color: 'amber', bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' }
];

export const STANDARD_LEAVE_ALLOWANCES = {
  annual: 15,
  sick: 30,
  family: 3
};

/**
 * Checks if a staff member is the company Owner (Dominic Sardinha).
 * Owner is exempt from late comings / late arrival tracking.
 */
export function isStaffOwner(staffMember, email = null) {
  const cleanEmail = (email || staffMember?.email || '').toLowerCase().trim();
  if (cleanEmail === 'dominic@abacustelecomm.co.za') return true;
  if (staffMember?.role === 'Owner' || staffMember?.title === 'Owner' || staffMember?.is_owner === true) return true;
  return false;
}

/**
 * Returns YYYY-MM-DD in South African Standard Time (SAST, UTC+2)
 */
export function getSastDateString(d = new Date()) {
  try {
    return d.toLocaleDateString('en-CA', { timeZone: 'Africa/Johannesburg' });
  } catch (e) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }
}

/**
 * Returns HH:mm in South African Standard Time (SAST, UTC+2)
 */
export function getSastTimeString(d = new Date()) {
  try {
    return d.toLocaleTimeString('en-GB', { 
      timeZone: 'Africa/Johannesburg', 
      hour: '2-digit', 
      minute: '2-digit', 
      hour12: false 
    });
  } catch (e) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
}

/**
 * Calculates after-hours work in strict 20-minute increments.
 * 1-20 mins = 1 increment (20m)
 * 21-40 mins = 2 increments (40m)
 * 41-60 mins = 3 increments (60m), etc.
 */
export function calculateAfterHoursIncrements(minutes = 0) {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m <= 0) {
    return { increments: 0, clockedMinutes: 0 };
  }
  const increments = Math.ceil(m / 20);
  return {
    increments,
    clockedMinutes: increments * 20
  };
}

/**
 * Checks if the current time in SAST is outside of standard scheduled shift hours.
 * Weekends (Saturday/Sunday) are always considered after-hours.
 * On weekdays, any time on or after shiftEndTime or before shiftStartTime is after-hours.
 */
export function isAfterHoursNow(staffMember = null, settings = DEFAULT_ATTENDANCE_SETTINGS, d = new Date(), email = null) {
  try {
    const cleanEmail = (email || staffMember?.email || '').toLowerCase().trim();
    const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';

    // Determine SAST Day of Week
    const sastDateStr = getSastDateString(d);
    const [year, month, day] = sastDateStr.split('-').map(Number);
    const sastDate = new Date(year, month - 1, day);
    const dayOfWeek = sastDate.getDay(); // 0 = Sun, 6 = Sat

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      return true; // Weekends are 100% after-hours
    }

    const timeStr = getSastTimeString(d);
    const [currH, currM] = timeStr.split(':').map(Number);
    const currentMinutes = currH * 60 + currM;

    // For technician@abacustelecomm.co.za: after hours strictly starts after 6pm (18:00) on weekdays
    if (isTechnician) {
      return currentMinutes >= 18 * 60;
    }

    const startStr = staffMember?.work_start_time || ((cleanEmail === 'dominic@abacustelecomm.co.za' || cleanEmail === 'technician@abacustelecomm.co.za') ? '07:30' : (settings?.default_start_time || '08:00'));
    const [startH, startM] = startStr.split(':').map(Number);
    const startMinutes = startH * 60 + startM;

    const endStr = staffMember?.work_end_time || settings?.default_end_time || '17:00';
    const [endH, endM] = endStr.split(':').map(Number);
    const endMinutes = endH * 60 + endM;

    // Evening after-hours: worked on or after shift end time
    if (currentMinutes >= endMinutes) {
      return true;
    }

    // Morning after-hours: ONLY count as after-hours if strictly before the morning cutoff (default 07:00 AM)
    // Any time between 07:00 AM and shift start (07:30 / 08:00) is regular morning arrival / prep, NOT after-hours.
    const morningCutoffStr = settings?.morning_cutoff_time || '07:00';
    const [mCutH, mCutM] = morningCutoffStr.split(':').map(Number);
    const morningCutoffMinutes = mCutH * 60 + mCutM; // 420 for 07:00

    if (currentMinutes < startMinutes && currentMinutes < morningCutoffMinutes) {
      return true;
    }

    return false;
  } catch (err) {
    console.warn('[attendanceService] Error checking after-hours:', err);
    return false;
  }
}

/**
 * Calculates minutes late comparing HH:mm strings
 */
export function calculateLateMinutes(clockInHHMM, shiftStartHHMM, graceMinutes = 5) {
  if (!clockInHHMM || !shiftStartHHMM) return 0;
  const [cHour, cMin] = clockInHHMM.split(':').map(Number);
  const [sHour, sMin] = shiftStartHHMM.split(':').map(Number);
  if (isNaN(cHour) || isNaN(cMin) || isNaN(sHour) || isNaN(sMin)) return 0;
  
  const clockInMinutes = cHour * 60 + cMin;
  const startMinutes = sHour * 60 + sMin;
  const rawDiff = clockInMinutes - startMinutes;
  
  // 5-minute grace period rule:
  // From shift start to shift start + 5m is grace (<= graceMinutes), counted as On Time (0m late).
  // Any arrival after the 5-minute grace (> graceMinutes) counts only the minutes past grace:
  if (rawDiff <= graceMinutes) {
    return 0;
  }
  return rawDiff - graceMinutes;
}

/**
 * Formats minutes into human-readable e.g. "1h 25m" or "45m"
 */
export function formatMinutes(mins = 0) {
  const m = Math.round(Number(mins) || 0);
  if (m <= 0) return '0m';
  if (m < 60) return `${m}m`;
  const hrs = Math.floor(m / 60);
  const rem = m % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
}

/**
 * Loads company attendance settings
 */
export async function getAttendanceSettings() {
  try {
    const ref = doc(db, 'app_settings', 'attendance_settings');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return { ...DEFAULT_ATTENDANCE_SETTINGS, ...snap.data() };
    }
  } catch (err) {
    console.warn('[attendanceService] Error loading settings:', err.message);
  }
  return DEFAULT_ATTENDANCE_SETTINGS;
}

/**
 * Saves company attendance settings
 */
export async function saveAttendanceSettings(settings) {
  const ref = doc(db, 'app_settings', 'attendance_settings');
  await setDoc(ref, {
    ...settings,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

/**
 * Records daily clock-in when user signs in.
 * Supports after-hours logins without penalizing with false late hours.
 */
export async function recordClockIn(user, staffMember = null, settings = DEFAULT_ATTENDANCE_SETTINGS) {
  if (!user || !user.email) return null;

  const dateStr = getSastDateString();
  const timeStr = getSastTimeString();
  const cleanEmail = user.email.toLowerCase().trim();
  let activeStaff = staffMember;
  if (!activeStaff?.id && cleanEmail) {
    try {
      const q = query(collection(db, 'staff'), where('email', '==', cleanEmail));
      const sSnap = await getDocs(q);
      if (!sSnap.empty) {
        activeStaff = { id: sSnap.docs[0].id, ...sSnap.docs[0].data() };
      }
    } catch (e) {}
  }

  const isOwner = isStaffOwner(activeStaff, cleanEmail);
  const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';

  // Fallback for Sales Tech Dominic De Groot (technician@) if start time is not yet configured
  if (isTechnician && !activeStaff?.work_start_time) {
    if (!activeStaff) activeStaff = {};
    activeStaff.work_start_time = '07:30';
  }

  const shiftStartTime = activeStaff?.work_start_time || staffMember?.work_start_time || settings?.default_start_time || '08:00';
  const shiftEndTime = activeStaff?.work_end_time || staffMember?.work_end_time || settings?.default_end_time || '17:00';
  const isAfterHours = isAfterHoursNow(activeStaff, settings, new Date(), cleanEmail);

  const staffId = activeStaff?.id || staffMember?.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const docId = `${staffId}_${dateStr}`;
  const docRef = doc(db, 'staff_attendance', docId);

  const emailDocId = `${cleanEmail.replace(/[^a-zA-Z0-9]/g, '_')}_${dateStr}`;
  const staffDocId = activeStaff?.id ? `${activeStaff.id}_${dateStr}` : null;

  try {
    // 1. Check if ANY record already exists for today under either doc ID or via email query
    let existingRecord = null;
    let existingDocId = null;

    const directSnap = await getDoc(docRef);
    if (directSnap.exists()) {
      existingRecord = directSnap.data();
      existingDocId = directSnap.id;
    }

    if (!existingRecord && staffDocId && staffDocId !== docId) {
      const staffSnap = await getDoc(doc(db, 'staff_attendance', staffDocId));
      if (staffSnap.exists()) {
        existingRecord = staffSnap.data();
        existingDocId = staffSnap.id;
      }
    }

    if (!existingRecord && emailDocId && emailDocId !== docId) {
      const emailSnap = await getDoc(doc(db, 'staff_attendance', emailDocId));
      if (emailSnap.exists()) {
        existingRecord = emailSnap.data();
        existingDocId = emailSnap.id;
      }
    }

    // Query fallback:
    if (!existingRecord) {
      const qToday = query(
        collection(db, 'staff_attendance'),
        where('email', '==', cleanEmail),
        where('date', '==', dateStr)
      );
      const qSnap = await getDocs(qToday);
      if (!qSnap.empty) {
        existingRecord = qSnap.docs[0].data();
        existingDocId = qSnap.docs[0].id;
      }
    }

    // 2. IF EXISTING RECORD FOUND -> PRESERVE CLOCK-IN TIME (NEVER OVERWRITE ON REFRESH)
    if (existingRecord) {
      let effectiveClockIn = existingRecord.clockInTime || timeStr;

      // Remedy technician Oct 26 morning arrival: true clock-in was 07:37 (2m late past 07:35 grace)
      if (isTechnician && dateStr === '2026-10-26' && effectiveClockIn > '07:37') {
        effectiveClockIn = '07:37';
      }

      let effectiveLate = isOwner ? 0 : calculateLateMinutes(effectiveClockIn, shiftStartTime);
      let effectiveStatus = isOwner ? 'present' : (effectiveLate > 0 ? 'late' : 'present');

      const corrected = {
        ...existingRecord,
        clockInTime: effectiveClockIn,
        shiftStartTime,
        shiftEndTime,
        minutesLate: effectiveLate,
        status: effectiveStatus,
        updatedAt: serverTimestamp()
      };

      if (isOwner) {
        corrected.isOwnerExempt = true;
      }

      // Sync across both doc IDs
      const promises = [setDoc(doc(db, 'staff_attendance', existingDocId), corrected, { merge: true })];
      if (emailDocId && emailDocId !== existingDocId) {
        promises.push(setDoc(doc(db, 'staff_attendance', emailDocId), corrected, { merge: true }));
      }
      if (staffDocId && staffDocId !== existingDocId) {
        promises.push(setDoc(doc(db, 'staff_attendance', staffDocId), corrected, { merge: true }));
      }
      await Promise.all(promises);

      return {
        record: corrected,
        isNewClockIn: false,
        isAfterHours,
        dateStr,
        timeStr: effectiveClockIn
      };
    }

    // 3. FIRST CLOCK IN OF THE DAY
    let initialClockIn = timeStr;
    if (isTechnician && dateStr === '2026-10-26' && initialClockIn > '07:37') {
      initialClockIn = '07:37';
    }

    let status = 'present';
    let minutesLate = 0;

    if (isOwner) {
      status = isAfterHours ? 'after_hours' : 'present';
      minutesLate = 0;
    } else if (isAfterHours) {
      status = 'after_hours';
      minutesLate = 0;
    } else {
      minutesLate = calculateLateMinutes(initialClockIn, shiftStartTime);
      status = minutesLate > 0 ? 'late' : 'present';
    }

    const newRecord = {
      id: docId,
      staffId: activeStaff?.id || staffMember?.id || null,
      staffName: activeStaff?.full_name || staffMember?.full_name || user.displayName || cleanEmail.split('@')[0],
      email: cleanEmail,
      date: dateStr,
      clockInTime: initialClockIn,
      clockInIso: new Date().toISOString(),
      clockOutTime: null,
      clockOutIso: null,
      shiftStartTime,
      shiftEndTime,
      status,
      minutesLate,
      isAfterHoursLogin: isAfterHours,
      afterHoursMinutes: 0,
      afterHoursIncrements: 0,
      afterHoursClockedMinutes: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    if (isOwner) {
      newRecord.isOwnerExempt = true;
    }

    // Mirror to both docIds immediately:
    const savePromises = [setDoc(docRef, newRecord, { merge: true })];
    if (emailDocId && emailDocId !== docId) {
      savePromises.push(setDoc(doc(db, 'staff_attendance', emailDocId), newRecord, { merge: true }));
    }
    if (staffDocId && staffDocId !== docId) {
      savePromises.push(setDoc(doc(db, 'staff_attendance', staffDocId), newRecord, { merge: true }));
    }
    await Promise.all(savePromises);

    return {
      record: newRecord,
      isNewClockIn: true,
      isAfterHours,
      dateStr,
      timeStr: initialClockIn
    };
  } catch (err) {
    console.error('[attendanceService] Failed to record clock-in:', err);
    return null;
  }
}

/**
 * Periodically or on session change, updates the after-hours work progress in 20-min increments
 */
export async function recordAfterHoursProgress(staffId, email, activeAfterHoursMinutes = 0) {
  if (!staffId && !email) return null;
  const dateStr = getSastDateString();
  const timeStr = getSastTimeString();
  const cleanEmail = (email || '').toLowerCase().trim();
  const sId = staffId || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const docId = `${sId}_${dateStr}`;

  const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';
  const { increments, clockedMinutes } = isTechnician
    ? calculateAfterHoursIncrements(activeAfterHoursMinutes)
    : { increments: activeAfterHoursMinutes, clockedMinutes: activeAfterHoursMinutes };

  try {
    const docRef = doc(db, 'staff_attendance', docId);
    const updatePayload = {
      afterHoursMinutes: activeAfterHoursMinutes,
      afterHoursIncrements: increments,
      afterHoursClockedMinutes: clockedMinutes,
      clockOutTime: timeStr,
      clockOutIso: new Date().toISOString(),
      lastActiveTime: timeStr,
      status: 'present',
      updatedAt: serverTimestamp()
    };
    await setDoc(docRef, updatePayload, { merge: true });

    // For technician@abacustelecomm.co.za: mirror to both doc IDs to keep everything in sync
    if (cleanEmail === 'technician@abacustelecomm.co.za') {
      const emailDocId = `technician_abacustelecomm_co_za_${dateStr}`;
      const staffDocId = `efa92ce6-609e-918e-3871-602d17e06a2b_${dateStr}`;
      const mirrorDocId = docId === emailDocId ? staffDocId : emailDocId;
      try {
        await setDoc(doc(db, 'staff_attendance', mirrorDocId), updatePayload, { merge: true });
      } catch (mErr) {
        // ignore mirror error
      }
    }

    return { increments, clockedMinutes, activeAfterHoursMinutes };
  } catch (err) {
    console.warn('[attendanceService] Failed to update after-hours progress:', err.message);
    return null;
  }
}

/**
 * Records clock-out on today's attendance record with optional final after-hours minutes
 */
export async function recordClockOut(staffId, email, finalAfterHoursMinutes = null) {
  if (!staffId && !email) return;
  const dateStr = getSastDateString();
  const timeStr = getSastTimeString();
  const cleanEmail = (email || '').toLowerCase().trim();
  const sId = staffId || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const docId = `${sId}_${dateStr}`;

  try {
    const docRef = doc(db, 'staff_attendance', docId);
    const updatePayload = {
      clockOutTime: timeStr,
      clockOutIso: new Date().toISOString(),
      updatedAt: serverTimestamp()
    };

    if (finalAfterHoursMinutes !== null && finalAfterHoursMinutes > 0) {
      const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';
      const { increments, clockedMinutes } = isTechnician
        ? calculateAfterHoursIncrements(finalAfterHoursMinutes)
        : { increments: finalAfterHoursMinutes, clockedMinutes: finalAfterHoursMinutes };
      updatePayload.afterHoursMinutes = finalAfterHoursMinutes;
      updatePayload.afterHoursIncrements = increments;
      updatePayload.afterHoursClockedMinutes = clockedMinutes;
    }

    await setDoc(docRef, updatePayload, { merge: true });
  } catch (err) {
    console.warn('[attendanceService] Failed to record clock-out:', err.message);
  }
}

/**
 * Updates individual staff member work hours in `staff` collection
 */
export async function updateStaffWorkHours(staffDocId, workStartTime, workEndTime) {
  if (!staffDocId) return;
  const ref = doc(db, 'staff', staffDocId);
  await updateDoc(ref, {
    work_start_time: workStartTime,
    work_end_time: workEndTime,
    updatedAt: serverTimestamp()
  });
}

/**
 * Calculates working days between two YYYY-MM-DD dates inclusive (excludes weekends).
 */
export function calculateWorkingDays(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return [];
  const [sY, sM, sD] = startDateStr.split('-').map(Number);
  const [eY, eM, eD] = endDateStr.split('-').map(Number);
  const start = new Date(sY, sM - 1, sD);
  const end = new Date(eY, eM - 1, eD);
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) return [];

  const workingDates = [];
  const cur = new Date(start);
  const pad = (n) => String(n).padStart(2, '0');

  while (cur <= end) {
    const day = cur.getDay(); // 0 = Sun, 6 = Sat
    if (day !== 0 && day !== 6) {
      workingDates.push(`${cur.getFullYear()}-${pad(cur.getMonth() + 1)}-${pad(cur.getDate())}`);
    }
    cur.setDate(cur.getDate() + 1);
  }
  return workingDates;
}

/**
 * Allocates an unlogged or absent day to AWOL or a Leave type (Annual, Sick, Family).
 */
export async function allocateAttendanceLeave({ staffMember, dateStr, leaveType, notes = '', allocatedBy = 'Super User' }) {
  if (!staffMember || !dateStr) throw new Error('Missing staff member or date');
  const cleanEmail = (staffMember.email || '').toLowerCase().trim();
  const staffId = staffMember.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const docId = `${staffId}_${dateStr}`;
  const docRef = doc(db, 'staff_attendance', docId);

  if (leaveType === 'clear' || leaveType === 'unallocated') {
    // Reset to absent
    await setDoc(docRef, {
      status: 'absent',
      leaveType: null,
      leaveLabel: null,
      notes: notes || '',
      updatedAt: serverTimestamp()
    }, { merge: true });
    return;
  }

  const found = LEAVE_TYPES.find(l => l.id === leaveType);
  const leaveLabel = found ? found.label : (leaveType || 'Leave');
  const status = leaveType === 'awol' ? 'awol' : 'leave';

  await setDoc(docRef, {
    id: docId,
    staffId: staffMember.id || null,
    staffName: staffMember.full_name || staffMember.first_name || cleanEmail.split('@')[0],
    email: cleanEmail,
    date: dateStr,
    status,
    leaveType,
    leaveLabel,
    notes,
    allocatedBy,
    allocatedAt: serverTimestamp(),
    clockInTime: '—',
    clockOutTime: '—',
    minutesLate: 0,
    shiftStartTime: staffMember.work_start_time || '08:00',
    shiftEndTime: staffMember.work_end_time || '17:00',
    updatedAt: serverTimestamp()
  }, { merge: true });
}

/**
 * Schedules staff leave across a date range and writes records to both staff_leave_schedules and staff_attendance
 */
export async function scheduleStaffLeave(staffMember, { leaveType, startDate, endDate, notes = '', scheduledBy = 'Super User' }) {
  if (!staffMember || !startDate || !endDate) throw new Error('Missing required fields for leave schedule');
  const cleanEmail = (staffMember.email || '').toLowerCase().trim();
  const staffId = staffMember.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const staffName = staffMember.full_name || staffMember.first_name || cleanEmail.split('@')[0];

  const workingDates = calculateWorkingDays(startDate, endDate);
  if (workingDates.length === 0) {
    throw new Error('No working days found in the selected date range (weekends are excluded).');
  }

  const found = LEAVE_TYPES.find(l => l.id === leaveType);
  const leaveLabel = found ? found.label : (leaveType || 'Leave');

  const scheduleId = `${staffId}_${startDate}_${endDate}_${Date.now()}`;
  const scheduleRef = doc(db, 'staff_leave_schedules', scheduleId);

  // 1. Save schedule entry
  await setDoc(scheduleRef, {
    id: scheduleId,
    staffId: staffMember.id || null,
    staffName,
    email: cleanEmail,
    leaveType,
    leaveLabel,
    startDate,
    endDate,
    workingDaysCount: workingDates.length,
    workingDates,
    notes,
    scheduledBy,
    status: 'approved',
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });

  // 2. Write attendance docs for each working day in the range
  const status = leaveType === 'awol' ? 'awol' : 'leave';
  const promises = workingDates.map(async (d) => {
    const docId = `${staffId}_${d}`;
    const docRef = doc(db, 'staff_attendance', docId);
    return setDoc(docRef, {
      id: docId,
      staffId: staffMember.id || null,
      staffName,
      email: cleanEmail,
      date: d,
      status,
      leaveType,
      leaveLabel,
      isScheduledLeave: true,
      scheduleId,
      notes,
      allocatedBy: scheduledBy,
      allocatedAt: serverTimestamp(),
      clockInTime: '—',
      clockOutTime: '—',
      minutesLate: 0,
      shiftStartTime: staffMember.work_start_time || '08:00',
      shiftEndTime: staffMember.work_end_time || '17:00',
      updatedAt: serverTimestamp()
    }, { merge: true });
  });

  await Promise.all(promises);
  return { scheduleId, workingDaysCount: workingDates.length, workingDates };
}

/**
 * Deletes / cancels a scheduled leave and removes generated attendance leave entries
 */
export async function deleteStaffLeaveSchedule(scheduleId, staffMember, workingDates = []) {
  if (!scheduleId) return;
  const cleanEmail = (staffMember?.email || '').toLowerCase().trim();
  const staffId = staffMember?.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');

  const scheduleRef = doc(db, 'staff_leave_schedules', scheduleId);
  await deleteDoc(scheduleRef);

  if (workingDates && workingDates.length > 0) {
    const promises = workingDates.map(async (d) => {
      const docId = `${staffId}_${d}`;
      const docRef = doc(db, 'staff_attendance', docId);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        const data = snap.data();
        if (data.isScheduledLeave && data.scheduleId === scheduleId) {
          await deleteDoc(docRef);
        }
      }
    });
    await Promise.all(promises);
  }
}

/**
 * Ensures any records from launch day (2026-10-22) are pardoned with 0 late minutes and present status.
 */
/**
 * Ensures Dominic Sardinha (Owner) has all late penalties removed across all existing records.
 */
/**
 * Remediation for Friday 2026-10-23 after hours:
 * Caps technician@abacustelecomm.co.za after-hours to 4 hours (240 minutes = 12 x 20m blocks) ending at 22:00.
 */
export async function remedyFridayAfterHours(cleanEmail) {
  if (!cleanEmail || cleanEmail.toLowerCase().trim() !== 'technician@abacustelecomm.co.za') return;
  try {
    const q = query(
      collection(db, 'staff_attendance'),
      where('email', '==', 'technician@abacustelecomm.co.za'),
      where('date', '==', '2026-10-23')
    );
    const snap = await getDocs(q);
    const promises = [];
    snap.forEach((dSnap) => {
      const data = dSnap.data();
      if ((Number(data.afterHoursMinutes) || 0) > 240 || data.clockOutTime === '23:59' || (Number(data.afterHoursClockedMinutes) || 0) > 240) {
        promises.push(
          updateDoc(doc(db, 'staff_attendance', dSnap.id), {
            afterHoursMinutes: 240,
            afterHoursIncrements: 12,
            afterHoursClockedMinutes: 240,
            clockOutTime: '22:00',
            notes: 'Adjusted to 4 hours (22:00 finish)',
            updatedAt: serverTimestamp()
          })
        );
      }
    });

    const docIds = ['efa92ce6-609e-918e-3871-602d17e06a2b_2026-10-23', 'technician_abacustelecomm_co_za_2026-10-23'];
    for (const dId of docIds) {
      try {
        const dRef = doc(db, 'staff_attendance', dId);
        const dSnap = await getDoc(dRef);
        if (dSnap.exists()) {
          const dData = dSnap.data();
          if ((Number(dData.afterHoursMinutes) || 0) > 240 || dData.clockOutTime === '23:59' || (Number(dData.afterHoursClockedMinutes) || 0) > 240) {
            promises.push(
              updateDoc(dRef, {
                afterHoursMinutes: 240,
                afterHoursIncrements: 12,
                afterHoursClockedMinutes: 240,
                clockOutTime: '22:00',
                notes: 'Adjusted to 4 hours (22:00 finish)',
                updatedAt: serverTimestamp()
              })
            );
          }
        }
      } catch (e) {}
    }

    if (promises.length > 0) {
      await Promise.all(promises);
    }
  } catch (err) {
    console.warn('[attendanceService] remedyFridayAfterHours notice:', err.message);
  }
}

export async function remedyOwnerAttendance(cleanEmail) {
  if (!cleanEmail) return;
  const isOwner = cleanEmail.toLowerCase().trim() === 'dominic@abacustelecomm.co.za';
  if (!isOwner) return;
  try {
    const q = query(
      collection(db, 'staff_attendance'),
      where('email', '==', cleanEmail.toLowerCase().trim())
    );
    const snap = await getDocs(q);
    const promises = [];
    snap.forEach((dSnap) => {
      const data = dSnap.data();
      if (data.status === 'late' || (Number(data.minutesLate) || 0) > 0) {
        promises.push(
          updateDoc(doc(db, 'staff_attendance', dSnap.id), {
            status: 'present',
            minutesLate: 0,
            isOwnerExempt: true,
            updatedAt: serverTimestamp()
          })
        );
      }
    });
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  } catch (err) {
    console.warn('[attendanceService] remedyOwnerAttendance notice:', err.message);
  }
}

export async function remedyLaunchDayAttendance(cleanEmail) {
  if (!cleanEmail) return;
  try {
    const q = query(
      collection(db, 'staff_attendance'),
      where('email', '==', cleanEmail.toLowerCase().trim()),
      where('date', '==', '2026-10-22')
    );
    const snap = await getDocs(q);
    const promises = [];
    snap.forEach((dSnap) => {
      const data = dSnap.data();
      if (data.status === 'late' || (Number(data.minutesLate) || 0) > 0) {
        promises.push(
          updateDoc(doc(db, 'staff_attendance', dSnap.id), {
            status: 'present',
            minutesLate: 0,
            launchDayAmnesty: true,
            updatedAt: serverTimestamp()
          })
        );
      }
    });
    if (promises.length > 0) {
      await Promise.all(promises);
    }
  } catch (err) {
    console.warn('[attendanceService] remedyLaunchDayAttendance notice:', err.message);
  }
}

/**
 * Excuses a late arrival for a staff member, removing the late penalty and marking as On Time (Excused).
 */
export async function excuseLateAttendance({ recordId, staffMember, dateStr, reason = '', adjustedClockIn = null, excusedBy = 'Super Admin' }) {
  if (!staffMember && !recordId) throw new Error('Missing recordId or staffMember');
  const cleanEmail = (staffMember?.email || '').toLowerCase().trim();
  const staffId = staffMember?.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const targetDocId = recordId || `${staffId}_${dateStr}`;

  const docRef = doc(db, 'staff_attendance', targetDocId);
  const snap = await getDoc(docRef);
  const prevData = snap.exists() ? snap.data() : {};

  const payload = {
    status: 'present',
    minutesLate: 0,
    isLateExcused: true,
    originalMinutesLate: prevData.originalMinutesLate || prevData.minutesLate || 0,
    excusedReason: reason || 'Approved by Management',
    excusedBy,
    excusedAt: serverTimestamp(),
    notes: reason ? `Excused: ${reason}` : (prevData.notes || 'Late arrival excused by management'),
    updatedAt: serverTimestamp()
  };

  if (adjustedClockIn && typeof adjustedClockIn === 'string' && adjustedClockIn.trim().length > 0) {
    payload.clockInTime = adjustedClockIn.trim();
  }

  await setDoc(docRef, payload, { merge: true });

  // For technician@abacustelecomm.co.za: mirror to both doc IDs to keep everything in sync
  if (cleanEmail === 'technician@abacustelecomm.co.za' || targetDocId.includes('technician_abacustelecomm_co_za') || targetDocId.includes('efa92ce6-609e-918e-3871-602d17e06a2b')) {
    const dStr = dateStr || prevData.date || getSastDateString();
    const emailDocId = `technician_abacustelecomm_co_za_${dStr}`;
    const staffDocId = `efa92ce6-609e-918e-3871-602d17e06a2b_${dStr}`;
    const mirrorId = targetDocId === emailDocId ? staffDocId : emailDocId;
    try {
      await setDoc(doc(db, 'staff_attendance', mirrorId), payload, { merge: true });
    } catch (mErr) {
      // ignore mirror error
    }
  }

  return { success: true, docId: targetDocId };
}

/**
 * Reverts an excused late record back to its original late status.
 */
export async function unexcuseLateAttendance({ recordId, staffMember, dateStr }) {
  const cleanEmail = (staffMember?.email || '').toLowerCase().trim();
  const staffId = staffMember?.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const targetDocId = recordId || `${staffId}_${dateStr}`;

  const docRef = doc(db, 'staff_attendance', targetDocId);
  const snap = await getDoc(docRef);
  if (!snap.exists()) return;
  const data = snap.data();

  const restoredMinutes = data.originalMinutesLate || 0;
  const payload = {
    status: restoredMinutes > 0 ? 'late' : 'present',
    minutesLate: restoredMinutes,
    isLateExcused: false,
    excusedReason: null,
    excusedBy: null,
    excusedAt: null,
    updatedAt: serverTimestamp()
  };

  await setDoc(docRef, payload, { merge: true });

  if (cleanEmail === 'technician@abacustelecomm.co.za' || targetDocId.includes('technician_abacustelecomm_co_za') || targetDocId.includes('efa92ce6-609e-918e-3871-602d17e06a2b')) {
    const dStr = dateStr || data.date || getSastDateString();
    const emailDocId = `technician_abacustelecomm_co_za_${dStr}`;
    const staffDocId = `efa92ce6-609e-918e-3871-602d17e06a2b_${dStr}`;
    const mirrorId = targetDocId === emailDocId ? staffDocId : emailDocId;
    try {
      await setDoc(doc(db, 'staff_attendance', mirrorId), payload, { merge: true });
    } catch (mErr) {}
  }
}

/**
 * Updates a staff member's custom leave counters (prior/historical leave taken) and custom allowances.
 */
export async function updateStaffLeaveCounters(staffMemberOrId, { leaveCounters = {}, leaveAllowances = {}, adjustmentNotes = '', adjustedBy = 'Super Admin' }) {
  let docId = typeof staffMemberOrId === 'string' ? staffMemberOrId : staffMemberOrId?.id;
  
  if (!docId && staffMemberOrId?.email) {
    const targetEmail = staffMemberOrId.email.toLowerCase().trim();
    const allStaff = await getDocs(collection(db, 'staff'));
    allStaff.forEach(d => {
      if ((d.data().email || '').toLowerCase().trim() === targetEmail) {
        docId = d.id;
      }
    });
  }

  if (!docId) {
    throw new Error('Could not identify staff document to update leave counters');
  }

  const staffRef = doc(db, 'staff', docId);
  const payload = {
    leave_counters: {
      annual_taken: Math.max(0, Number(leaveCounters?.annual_taken) || 0),
      sick_taken: Math.max(0, Number(leaveCounters?.sick_taken) || 0),
      family_taken: Math.max(0, Number(leaveCounters?.family_taken) || 0),
      awol_taken: Math.max(0, Number(leaveCounters?.awol_taken) || 0)
    },
    leave_allowances: {
      annual: Math.max(1, Number(leaveAllowances?.annual) || STANDARD_LEAVE_ALLOWANCES.annual),
      sick: Math.max(1, Number(leaveAllowances?.sick) || STANDARD_LEAVE_ALLOWANCES.sick),
      family: Math.max(0, Number(leaveAllowances?.family) ?? STANDARD_LEAVE_ALLOWANCES.family)
    },
    leave_adjustment_notes: adjustmentNotes || '',
    leave_adjusted_by: adjustedBy,
    leave_adjusted_at: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(staffRef, payload, { merge: true });
  return payload;
}


/**
 * Clears after-hours recorded minutes and increments for a staff member on a specific date (Boss / Super Admin tool).
 */
/**
 * Manually adjusts after-hours minutes, increments, and clock-out time for any day.
 */
export async function adjustAfterHoursAttendance({ recordId, staffMember, dateStr, minutes = 0, clockOutTime = null, adjustedBy = 'Super Admin', notes = '' }) {
  if (!staffMember && !recordId) throw new Error('Missing recordId or staffMember');
  const cleanEmail = (staffMember?.email || '').toLowerCase().trim();
  const isTechnician = cleanEmail === 'technician@abacustelecomm.co.za';
  const staffId = staffMember?.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const targetDocId = recordId || `${staffId}_${dateStr}`;

  const numMins = Math.max(0, Number(minutes) || 0);
  const { increments, clockedMinutes } = isTechnician
    ? calculateAfterHoursIncrements(numMins)
    : { increments: numMins, clockedMinutes: numMins };

  const payload = {
    afterHoursMinutes: numMins,
    afterHoursIncrements: increments,
    afterHoursClockedMinutes: clockedMinutes,
    afterHoursAdjustedBy: adjustedBy,
    afterHoursAdjustedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  if (clockOutTime) {
    payload.clockOutTime = clockOutTime;
  }
  if (notes) {
    payload.notes = notes;
  }

  const docRef = doc(db, 'staff_attendance', targetDocId);
  await setDoc(docRef, payload, { merge: true });

  const dStr = dateStr || getSastDateString();
  const emailDocId = `${cleanEmail.replace(/[^a-zA-Z0-9]/g, '_')}_${dStr}`;
  const staffDocId = staffMember?.id ? `${staffMember.id}_${dStr}` : null;
  const otherId = targetDocId === emailDocId ? staffDocId : emailDocId;
  if (otherId && otherId !== targetDocId) {
    try {
      await setDoc(doc(db, 'staff_attendance', otherId), payload, { merge: true });
    } catch (e) {}
  }

  return { success: true, docId: targetDocId, minutes: numMins, increments, clockedMinutes };
}

export async function clearAfterHoursAttendance({ recordId, staffMember, dateStr, clearedBy = 'Super Admin' }) {
  if (!staffMember && !recordId) throw new Error('Missing recordId or staffMember');
  const cleanEmail = (staffMember?.email || '').toLowerCase().trim();
  const staffId = staffMember?.id || cleanEmail.replace(/[^a-zA-Z0-9]/g, '_');
  const targetDocId = recordId || `${staffId}_${dateStr}`;

  const payload = {
    afterHoursMinutes: 0,
    afterHoursIncrements: 0,
    afterHoursClockedMinutes: 0,
    isAfterHoursLogin: false,
    afterHoursCleared: true,
    afterHoursClearedBy: clearedBy,
    afterHoursClearedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  const docRef = doc(db, 'staff_attendance', targetDocId);
  await setDoc(docRef, payload, { merge: true });

  const dStr = dateStr || getSastDateString();
  const emailDocId = `${cleanEmail.replace(/[^a-zA-Z0-9]/g, '_')}_${dStr}`;
  const staffDocId = staffMember?.id ? `${staffMember.id}_${dStr}` : null;
  const otherId = targetDocId === emailDocId ? staffDocId : emailDocId;
  if (otherId && otherId !== targetDocId) {
    try {
      await setDoc(doc(db, 'staff_attendance', otherId), payload, { merge: true });
    } catch (e) {}
  }

  return { success: true, docId: targetDocId };
}

/**
 * Automatically audits and repairs today's attendance records across all staff:
 * 1. Preserves earliest clock-in time of the day (prevents page refresh overrides).
 * 2. Enforces Dominic De Groot's morning clock-in at 07:37 (2m late past 07:35 grace).
 * 3. Exempts Dominic Sardinha (Owner) from late penalties.
 * 4. Ensures accurate minutesLate with 5-minute grace period across all staff.
 */
export async function remedyAllStaffAttendanceToday() {
  try {
    const todayStr = getSastDateString();
    const q = query(
      collection(db, 'staff_attendance'),
      where('date', '==', todayStr)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;

    const recordsByEmail = new Map();
    snap.forEach((dSnap) => {
      const data = dSnap.data();
      const email = (data.email || '').toLowerCase().trim();
      if (!email) return;
      if (!recordsByEmail.has(email)) {
        recordsByEmail.set(email, []);
      }
      recordsByEmail.get(email).push({ id: dSnap.id, ...data });
    });

    for (const [email, docs] of recordsByEmail.entries()) {
      const isOwner = email === 'dominic@abacustelecomm.co.za';
      const isTechnician = email === 'technician@abacustelecomm.co.za';

      // Find earliest clockInTime among all docs
      let earliestClockIn = null;
      for (const d of docs) {
        if (d.clockInTime && d.clockInTime !== '—') {
          if (!earliestClockIn || d.clockInTime < earliestClockIn) {
            earliestClockIn = d.clockInTime;
          }
        }
      }

      // For technician@abacustelecomm.co.za on 2026-10-26, true morning arrival was 07:37
      if (isTechnician && (!earliestClockIn || earliestClockIn > '07:37')) {
        earliestClockIn = '07:37';
      }

      if (!earliestClockIn) continue;

      const shiftStart = docs[0].shiftStartTime || (isTechnician ? '07:30' : '08:00');
      let correctLateMins = 0;
      let correctStatus = 'present';

      if (isOwner) {
        correctLateMins = 0;
        correctStatus = 'present';
      } else {
        correctLateMins = calculateLateMinutes(earliestClockIn, shiftStart);
        correctStatus = correctLateMins > 0 ? 'late' : 'present';
      }

      // Update all docs for this email today that have incorrect clockIn or minutesLate
      for (const d of docs) {
        const needsUpdate = d.clockInTime !== earliestClockIn || 
                            (Number(d.minutesLate) || 0) !== correctLateMins || 
                            d.status !== correctStatus;
        if (needsUpdate) {
          try {
            await updateDoc(doc(db, 'staff_attendance', d.id), {
              clockInTime: earliestClockIn,
              minutesLate: correctLateMins,
              status: correctStatus,
              updatedAt: serverTimestamp()
            });
          } catch (uErr) {
            console.warn('[attendanceService] Failed to update doc:', d.id, uErr.message);
          }
        }
      }
    }
  } catch (err) {
    console.warn('[attendanceService] remedyAllStaffAttendanceToday error:', err.message);
  }
}
